import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Container from 'reactstrap/lib/Container';
import classNames from 'classnames';
import { Link } from 'site-modules/shared/components/link/link';
import { getDealerWebsiteExternalLink } from 'site-modules/shared/utils/dealership-link-constructor';
import { clearPhone, createNicePhone } from 'site-modules/shared/utils/phone-format';
import { srpLinkBuilder } from 'site-modules/shared/utils/srp-link-constructor';
import { DealerInfoEntities, DealerInfoModel } from 'client/data/models/dealer-info';
import { connect } from 'react-redux';
import { connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { getGoogleMapsUrl } from 'site-modules/shared/utils/dealer-details-utils';
import { showChatButton } from 'site-modules/dealerships/utils/show-chat-button';
import { useActivEngageChat } from 'site-modules/shared/hooks/activ-engage-chat/use-activ-engage-chat';

import './dealer-info-content.scss';

const PHONE_PLACEHOLDER = /1{10}/;

export const DealerInfoContentUI = ({ dealerInfo, dealerChatInfo, isMobile }) => {
  const [isCTAClicked, setIsCTAClicked] = useState(false);
  const { rooftopId: dealerId, address, name: dealerName, dealerWebSites = [], parentDealershipName } = dealerInfo;

  const handleClickCTATextChange = useCallback(e => {
    e.preventDefault();
    setIsCTAClicked(true);
  }, []);

  const getDealerPhone = useCallback(() => {
    const { phones } = dealerInfo;
    const dealerPhone = phones && phones.length && phones.find(phone => !PHONE_PLACEHOLDER.test(phone));
    return (dealerPhone && dealerPhone.replace(' ', '')) || null;
  }, [dealerInfo]);

  const dealerPhone = getDealerPhone();
  let nicePhone;
  let phoneClear;

  if (dealerPhone) {
    nicePhone = createNicePhone(dealerPhone);
    phoneClear = clearPhone(dealerPhone);
  }

  const dealerWebSite = getDealerWebsiteExternalLink({
    dealerWebSites,
    dealerName,
    city: address?.city,
    stateCode: address?.stateCode,
  });
  const { dealerSmsNumber, isEdmundsChatActive } = dealerChatInfo;
  const hasChatButton = showChatButton({
    isMobile,
    parentDealershipName,
    dealerSmsNumber,
    isEdmundsChatActive,
  });

  const { onStartChat } = useActivEngageChat({ isEnabled: hasChatButton, dealerWebSite, launchSource: 'edmunds-ddp' });

  const linksMap = [
    {
      to: srpLinkBuilder({ dealerlocationid: dealerId }),
      title: 'Inventory',
      'data-tracking-id': 'vdp_view_inventory',
      icon: 'icon-price-tags2',
      iconClasses: 'size-14 me-0_25',
      icon2: 'icon-arrow-right4',
    },
    {
      isVisible: hasChatButton,
      title: 'Chat with us',
      icon: 'icon-bubbles',
      icon2: 'icon-arrow-right4',
      onClick: onStartChat,
    },
    {
      isVisible: isMobile && dealerSmsNumber,
      to: `sms:${dealerSmsNumber}`,
      title: 'Text us',
      'data-tracking-id': 'ddp_text_us',
      icon: 'icon-mobile',
      icon2: 'icon-open_in_new',
    },
    {
      isVisible: dealerPhone,
      to: `tel:${phoneClear}`,
      title: isCTAClicked ? nicePhone : 'Call us',
      onClick: isCTAClicked ? null : handleClickCTATextChange,
      'data-tracking-id': 'ddp_call_dealer',
      'data-tracking-value': nicePhone,
      'data-testid': 'dealer-phone',
      icon: 'icon-phone2',
      icon2: 'icon-arrow-right4',
    },
    {
      to: getGoogleMapsUrl(address),
      rel: 'noopener noreferrer nofollow',
      title: 'Directions',
      target: '_blank',
      'data-tracking-id': 'ddp_view_dealer_hours_and_directions',
      'data-testid': 'dealer-address',
      icon: 'icon-location',
      icon2: 'icon-open_in_new',
    },
    {
      isVisible: !!dealerWebSite,
      to: dealerWebSite,
      rel: 'noopener noreferrer nofollow',
      title: 'Visit our site',
      'data-tracking-id': 'ddp_dealer_site_clickout',
      target: '_blank',
      icon: 'icon-car',
      icon2: 'icon-open_in_new',
    },
  ];

  return (
    <Container className="dealer-info-content">
      <ul className="dealer-info-content-links list-unstyled d-flex justify-content-around text-cool-gray-10 mx-0 mb-1 pb-0_5 px-0_5 fw-medium">
        {linksMap.map(
          (
            {
              isVisible = true,
              to,
              rel,
              title,
              onClick,
              'data-tracking-id': dataTrackingId,
              'data-tracking-value': dataTrackingValue,
              'data-testid': dataTestId,
              target,
              icon,
              iconClasses,
              icon2,
            },
            index
          ) =>
            isVisible && (
              <li
                key={title}
                className={classNames({ 'ms-1': !!index }, 'm-0 bg-white contact-item rounded-16 p-1 col')}
              >
                <Link
                  to={to}
                  rel={rel}
                  target={target}
                  className={classNames(
                    'text-decoration-none d-flex align-items-center justify-content-center justify-content-lg-between'
                  )}
                  onClick={onClick}
                  data-tracking-id={dataTrackingId}
                  data-tracking-value={dataTrackingValue}
                  data-testid={dataTestId}
                >
                  <div className="d-flex align-items-center flex-column flex-lg-row">
                    <div className="circle-btn bg-blue-50 rounded-circle d-inline-flex justify-content-center align-items-center me-lg-0_75">
                      <i className={classNames(icon, iconClasses, 'text-white')} aria-hidden />
                    </div>
                    <span className="mt-1 mt-lg-0 text-nowrap">
                      {title}
                      <i className={classNames(icon2, 'text-cool-gray-30 hidden-lg-up size-12 ms-0_5')} aria-hidden />
                    </span>
                  </div>
                  <i className={classNames(icon2, 'text-primary-darker hidden-lg-down')} aria-hidden />
                </Link>
              </li>
            )
        )}
      </ul>
    </Container>
  );
};
DealerInfoContentUI.propTypes = {
  dealerInfo: DealerInfoEntities.DealerInfo,
  dealerChatInfo: PropTypes.shape({
    dealerSmsNumber: PropTypes.string,
    isEdmundsChatActive: PropTypes.bool,
  }),
  isMobile: PropTypes.bool,
};

DealerInfoContentUI.defaultProps = {
  dealerInfo: {},
  dealerChatInfo: { dealerSmsNumber: '', isEdmundsChatActive: false },
  isMobile: false,
};

export const stateToPropsConfig = {
  dealerChatInfo: {
    path: ({ dealerInfo }) => {
      const rooftopId = dealerInfo?.rooftopId;
      return !!rooftopId && `carcodeConfig.${rooftopId}`;
    },
    segment: DealerInfoModel,
    transform: ({ smsNumber, isEdmundsChatActive }) => ({ dealerSmsNumber: smsNumber, isEdmundsChatActive }),
  },
};

export const mapStateToProps = state => ({
  isMobile: state.mobile,
});

export const DealerInfoContent = connect(mapStateToProps)(connectToModel(DealerInfoContentUI, stateToPropsConfig));
